import React, { useCallback, useEffect, forwardRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'

import { reloadSchema } from '@galo-monitor/forms'
import stockSchema from '@galo-monitor/forms/schemas/farm/stock.schema'
import { useT } from '@galo-monitor/i18n'
import { useSnackbar } from '@galo-monitor/snackbar'
import { selectCurrentProperty } from '@galo-monitor/stores/property/selectorProperty'
import { RawMaterialsStocksActions } from '@galo-monitor/stores/rawMaterialsStocks'
import Form from '@galo-monitor/web-components/Form'
import InputText from '@galo-monitor/web-components/InputText'

const StockForm = forwardRef((props, formRef) => {
  const { stock, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { id: currentPropertyId } = useSelector(selectCurrentProperty)

  const revalidateSchema = useCallback(
    () => reloadSchema(formRef, true),
    [formRef]
  )

  const reloadPropertySelect = useCallback(
    () => {
      revalidateSchema()
      const propertiesRef = formRef.current.getFieldRef('properties_ids')
      propertiesRef.clearError && propertiesRef.clearError()
    },
    [formRef, revalidateSchema]
  )

  const handleSubmit = useCallback(
    (data) => {
      setLoading(true)
      dispatch(RawMaterialsStocksActions.saveStock(
        {
          ...stock,
          ...data,
          properties_ids: [String(currentPropertyId)],
          is_always_access_properties: false
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('stock', { howMany: 1 }),
            action: toLower(t(isEmpty(stock) ? 'created' : 'updated', { gender: 'female', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [setLoading, dispatch, stock, currentPropertyId, onError, snackbar, t, onSuccess]
  )

  useEffect(
    () => {
      if (stock?.id && !stock.isAlwaysAccessProperties) {
        reloadPropertySelect()
      }
    },
    [reloadPropertySelect, stock]
  )

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ stockSchema }
      onSubmit={ handleSubmit }
    >
      <Grid container spacing={ 2 }>
        <Grid item xs={ 12 }>
          <InputText
            name="name"
            label={ t('name of stock') }
            defaultValue={ stock.name }
            disabled={ loading }
            fullWidth
          />
        </Grid>
      </Grid>
    </Form>
  )
})

StockForm.propTypes = {
  stock: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

StockForm.defaultProps = {
  stock: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default StockForm
